import React, { useState, useEffect } from "react";

import { dashboardAPI } from "../services/api";
import TopHeader from "../components/TopHeader.jsx";
import KPIGrid from "../components/KPIGrid.jsx";
import QuickLinks from "../components/QuickLinks.jsx";
import RecentErrorReports from "../components/RecentErrorReports.jsx";
import ErrorDistributionChart from "../components/ErrorDistributionChart.jsx";
import ActivityTimeline from "../components/ActivityTimeline.jsx";
import RecentSubmissions from "../components/RecentSubmissions.jsx";
import { dashboardCache } from "../utils/cache";
import PasswordProtection from "../components/PasswordProtection.jsx";

// Password hash - in production, this should be stored more securely
// Password: admin123

const GlobalDashboard = () => {
   const [dashboardData, setDashboardData] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [resolutionFilter, setResolutionFilter] = useState("");
   const [isBackgroundRefresh, setIsBackgroundRefresh] = useState(false);

   useEffect(() => {
      loadDashboardData();
   }, []);

   const loadDashboardData = async () => {
      // Try to get cached data first
      console.log("📊 Dashboard: Starting data load");
      const cachedData = dashboardCache.getData();

      if (cachedData) {
         console.log("📊 Dashboard: Using cached data");
         setDashboardData(cachedData);
         setLoading(false);

         // If cache needs refresh, update in background
         if (dashboardCache.needsRefresh()) {
            console.log("📊 Dashboard: Starting background refresh");
            setIsBackgroundRefresh(true);
            await fetchDashboardData();
            setIsBackgroundRefresh(false);
            console.log("📊 Dashboard: Background refresh complete");
         }
      } else {
         console.log("📊 Dashboard: No cache available, fetching fresh data");
         await fetchDashboardData();
      }
   };

   const fetchDashboardData = async () => {
      try {
         // Only show loading state if no data is displayed
         if (!dashboardData) {
            console.log("📊 Dashboard: Showing loading state");
            setLoading(true);
         }

         console.log("📊 Dashboard: Fetching from API");
         const response = await dashboardAPI.getDashboardData();

         // Update cache and state
         console.log("📊 Dashboard: Updating data");
         dashboardCache.setData(response);
         setDashboardData(response);
         setError("");
      } catch (err) {
         console.error("❌ Dashboard: Error fetching data:", err);
         setError("Failed to load dashboard data");
      } finally {
         setLoading(false);
      }
   };

   const applyResolutionFilter = (filterValue) => {
      setResolutionFilter(filterValue === "all" ? "" : filterValue);
   };

   if (loading && !dashboardData) {
      return (
         <div className="loading-container">
            <div className="loading-spinner"></div>
         </div>
      );
   }

   if (error && !dashboardData) {
      return (
         <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
            <div className="alert alert-danger">{error}</div>
         </div>
      );
   }

   const { stats, error_report_stats, error_reports, recent_submissions, reports_by_type } =
      dashboardData || {};

   // DEBUG: Check what is being passed down
   console.log("Data for chart in GlobalDashboard:", reports_by_type);

   const currentDate = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
   });

   function displayDashboard() {
      return (
         <div className="dashboard-wrapper">
            <main className="main-content">
               {/* Top Header */}
               <TopHeader
                  title="Global Dashboard"
                  icon="bi bi-clipboard-data"
                  date={currentDate}
                  onRefresh={loadDashboardData}
                  isRefreshing={isBackgroundRefresh}
               />

               {/* Content Area */}
               <div className="content-area">
                  {error && (
                     <div className="alert alert-danger mb-4" role="alert">
                        <i className="bi bi-exclamation-triangle-fill"></i>
                        <strong>Error:</strong> {error}
                     </div>
                  )}

                  {/* Background refresh indicator */}
                  {isBackgroundRefresh && (
                     <div className="text-center mb-2">
                        <small className="text-muted">
                           <i className="bi bi-arrow-repeat spin me-1"></i>
                           Refreshing data...
                        </small>
                     </div>
                  )}

                  {/* KPI Cards */}
                  <KPIGrid stats={stats} error_report_stats={error_report_stats} />

                  {/* Quick Access Links */}
                  <QuickLinks />
                  <div className="quick-links-divider" />

                  {/* Content Grid */}
                  <div className="content-grid">
                     {/* Recent Error Reports */}
                     <RecentErrorReports
                        error_reports={error_reports}
                        resolutionFilter={resolutionFilter}
                        applyResolutionFilter={applyResolutionFilter}
                     />

                     {/* Chart and Recent Submissions */}
                     <ErrorDistributionChart reports_by_type={reports_by_type} />
                     <ActivityTimeline stats={stats} />
                  </div>

                  {/* Recent Submissions */}
                  <RecentSubmissions recent_submissions={recent_submissions} isExpandable={true} />
               </div>
            </main>
         </div>
      );
   }

   return <PasswordProtection>{displayDashboard()}</PasswordProtection>;
};

export default GlobalDashboard;
